import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Plus, Trash2, Download, Printer } from 'lucide-react';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';

const Billing = () => {
  const [clientDetails, setClientDetails] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
  });

  const [items, setItems] = useState([
    { description: '', quantity: 1, rate: 0, amount: 0 },
  ]);

  const addItem = () => {
    setItems([...items, { description: '', quantity: 1, rate: 0, amount: 0 }]);
  };

  const removeItem = (index) => {
    const newItems = items.filter((_, i) => i !== index);
    setItems(newItems.length > 0 ? newItems : [{ description: '', quantity: 1, rate: 0, amount: 0 }]);
  };

  const updateItem = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    if (field === 'quantity' || field === 'rate') {
      newItems[index].amount = newItems[index].quantity * newItems[index].rate;
    }
    setItems(newItems);
  };

  const calculateSubtotal = () => {
    return items.reduce((sum, item) => sum + item.amount, 0);
  };

  const calculateGST = () => {
    return calculateSubtotal() * 0.18;
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateGST();
  };

  const generatePDF = () => {
    const doc = new jsPDF();
    const invoiceNumber = Date.now().toString().slice(-6);
    const invoiceDate = new Date().toLocaleDateString('en-IN');

    // Header - Company Name
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(45, 60, 146);
    doc.text('ANKU TAX CONSULTANT', 14, 20);

    // Company Details
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);
    doc.text('VIJAYNAGAR INDORE', 14, 27);
    doc.text('Phone: 6268642514', 14, 32);
    doc.text('Email: VERMATAXCON121@GMAIL.COM', 14, 37);
    doc.text('State: 23-Madhya Pradesh', 14, 42);

    // Tax Invoice Label (Right side)
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(45, 60, 146);
    doc.text('Tax Invoice', 160, 20);

    // Invoice Details Box (Right side)
    doc.setFillColor(248, 250, 252);
    doc.rect(130, 28, 66, 20, 'F');
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('Invoice Details:', 133, 33);
    doc.setFont('helvetica', 'normal');
    doc.text(`No: ${invoiceNumber}`, 133, 38);
    doc.text(`Date: ${invoiceDate}`, 133, 43);

    // Bill To Section
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Bill To:', 14, 58);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text(clientDetails.name || 'N/A', 14, 64);

    // Line Items Table
    const tableData = items.map((item, index) => [
      index + 1,
      item.description,
      '',
      item.quantity,
      `₹${item.rate.toFixed(2)}`,
      `₹${item.amount.toFixed(2)}`,
    ]);

    doc.autoTable({
      startY: 75,
      head: [['#', 'Item name', 'HSN/SAC', 'Quantity', 'Price/Unit(₹)', 'Amount(₹)']],
      body: tableData,
      theme: 'plain',
      headStyles: {
        fillColor: [248, 250, 252],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        fontSize: 9,
        lineWidth: 0.5,
        lineColor: [200, 200, 200],
      },
      bodyStyles: {
        fontSize: 9,
        lineWidth: 0.5,
        lineColor: [200, 200, 200],
      },
      columnStyles: {
        0: { cellWidth: 10, halign: 'center' },
        1: { cellWidth: 70 },
        2: { cellWidth: 20 },
        3: { cellWidth: 20, halign: 'center' },
        4: { cellWidth: 30, halign: 'right' },
        5: { cellWidth: 30, halign: 'right' },
      },
    });

    const finalY = doc.lastAutoTable.finalY + 5;

    // Totals Section (Right aligned)
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(`Sub Total`, 130, finalY + 5);
    doc.text(`₹${calculateSubtotal().toFixed(2)}`, 196, finalY + 5, { align: 'right' });

    doc.setFont('helvetica', 'bold');
    doc.text(`Total`, 130, finalY + 12);
    doc.text(`₹${calculateSubtotal().toFixed(2)}`, 196, finalY + 12, { align: 'right' });

    // Amount in Words
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text('Invoice Amount in Words:', 14, finalY + 25);
    doc.setFont('helvetica', 'normal');
    const amountInWords = numberToWords(calculateSubtotal()) + ' Rupees only';
    doc.text(amountInWords, 14, finalY + 30);

    // Payment Status
    doc.text('Received: ₹0.00', 14, finalY + 40);
    doc.text(`Balance: ₹${calculateSubtotal().toFixed(2)}`, 14, finalY + 45);

    // Terms & Conditions
    doc.setFont('helvetica', 'bold');
    doc.text('Terms & Conditions:', 14, finalY + 55);
    doc.setFont('helvetica', 'normal');
    doc.text('Thanks for doing business with us!', 14, finalY + 60);

    // Bank Details (Bottom Left)
    doc.setFont('helvetica', 'bold');
    doc.text('Bank Details:', 14, finalY + 70);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text('KOTAK MAHINDRA BANK LIMITED, VIJAY NAGAR BRANCH INDORE', 14, finalY + 75);
    doc.text('Account No.: 7447697985', 14, finalY + 80);
    doc.text('IFSC code: KKBK0005915', 14, finalY + 85);
    doc.text("Account holder's name: ANKESH JATAV", 14, finalY + 90);

    // Authorized Signatory (Bottom Right)
    doc.setFontSize(9);
    doc.text('For ANKU TAX CONSULTANT:', 130, finalY + 75);
    doc.setFont('helvetica', 'bold');
    doc.text('Authorized Signatory', 130, finalY + 90);

    doc.save(`Tax_Invoice_${invoiceNumber}.pdf`);
  };

  // Helper function to convert number to words (Indian format)
  const numberToWords = (num) => {
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];

    if (num === 0) return 'Zero';

    const convert = (n) => {
      if (n < 10) return ones[n];
      if (n < 20) return teens[n - 10];
      if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 !== 0 ? ' ' + ones[n % 10] : '');
      if (n < 1000) return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 !== 0 ? ' ' + convert(n % 100) : '');
      if (n < 100000) return convert(Math.floor(n / 1000)) + ' Thousand' + (n % 1000 !== 0 ? ' ' + convert(n % 1000) : '');
      return convert(Math.floor(n / 100000)) + ' Lakh' + (n % 100000 !== 0 ? ' ' + convert(n % 100000) : '');
    };

    return convert(Math.floor(num));
  };

  const printInvoice = () => {
    window.print();
  };

  return (
    <div className="pt-32 pb-20" data-testid="billing-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1 className="text-4xl sm:text-5xl font-bold text-[#0F172A] mb-8 text-center" data-testid="billing-title">
            Invoice Generator
          </h1>

          {/* Client Details */}
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-100 mb-8">
            <h2 className="text-2xl font-bold text-[#0F172A] mb-6">Client Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="clientName">Client Name</Label>
                <Input
                  id="clientName"
                  placeholder="Enter client name"
                  value={clientDetails.name}
                  onChange={(e) => setClientDetails({ ...clientDetails, name: e.target.value })}
                  data-testid="client-name-input"
                />
              </div>
              <div>
                <Label htmlFor="clientEmail">Email</Label>
                <Input
                  id="clientEmail"
                  type="email"
                  placeholder="client@example.com"
                  value={clientDetails.email}
                  onChange={(e) => setClientDetails({ ...clientDetails, email: e.target.value })}
                  data-testid="client-email-input"
                />
              </div>
              <div>
                <Label htmlFor="clientPhone">Phone</Label>
                <Input
                  id="clientPhone"
                  placeholder="Enter phone number"
                  value={clientDetails.phone}
                  onChange={(e) => setClientDetails({ ...clientDetails, phone: e.target.value })}
                  data-testid="client-phone-input"
                />
              </div>
              <div>
                <Label htmlFor="clientAddress">Address</Label>
                <Input
                  id="clientAddress"
                  placeholder="Enter address"
                  value={clientDetails.address}
                  onChange={(e) => setClientDetails({ ...clientDetails, address: e.target.value })}
                  data-testid="client-address-input"
                />
              </div>
            </div>
          </div>

          {/* Invoice Items */}
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-100 mb-8">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-[#0F172A]">Invoice Items</h2>
              <Button
                onClick={addItem}
                className="bg-[#10B981] hover:bg-[#047857]"
                data-testid="add-item-button"
              >
                <Plus size={18} className="mr-2" />
                Add Item
              </Button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full" data-testid="items-table">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-2 font-semibold text-slate-700">Description</th>
                    <th className="text-left py-3 px-2 font-semibold text-slate-700 w-24">Quantity</th>
                    <th className="text-left py-3 px-2 font-semibold text-slate-700 w-32">Rate (₹)</th>
                    <th className="text-left py-3 px-2 font-semibold text-slate-700 w-32">Amount (₹)</th>
                    <th className="w-12"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, index) => (
                    <tr key={index} className="border-b border-slate-100">
                      <td className="py-3 px-2">
                        <Input
                          placeholder="Service description"
                          value={item.description}
                          onChange={(e) => updateItem(index, 'description', e.target.value)}
                          data-testid={`item-description-${index}`}
                        />
                      </td>
                      <td className="py-3 px-2">
                        <Input
                          type="number"
                          min="1"
                          value={item.quantity}
                          onChange={(e) => updateItem(index, 'quantity', parseInt(e.target.value) || 1)}
                          data-testid={`item-quantity-${index}`}
                        />
                      </td>
                      <td className="py-3 px-2">
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.rate}
                          onChange={(e) => updateItem(index, 'rate', parseFloat(e.target.value) || 0)}
                          data-testid={`item-rate-${index}`}
                        />
                      </td>
                      <td className="py-3 px-2">
                        <div className="font-semibold text-[#0F172A]">
                          ₹{item.amount.toFixed(2)}
                        </div>
                      </td>
                      <td className="py-3 px-2">
                        <button
                          onClick={() => removeItem(index)}
                          className="text-red-500 hover:text-red-700"
                          data-testid={`remove-item-${index}`}
                        >
                          <Trash2 size={18} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Invoice Summary */}
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-100 mb-8">
            <h2 className="text-2xl font-bold text-[#0F172A] mb-6">Invoice Summary</h2>
            <div className="space-y-3">
              <div className="flex justify-between text-lg">
                <span className="text-slate-600">Subtotal:</span>
                <span className="font-semibold text-[#0F172A]" data-testid="subtotal-amount">
                  ₹{calculateSubtotal().toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between text-lg">
                <span className="text-slate-600">GST (18%):</span>
                <span className="font-semibold text-[#0F172A]" data-testid="gst-amount">
                  ₹{calculateGST().toFixed(2)}
                </span>
              </div>
              <div className="border-t border-slate-200 pt-3 flex justify-between text-xl">
                <span className="font-bold text-[#0F172A]">Total:</span>
                <span className="font-bold text-[#10B981]" data-testid="total-amount">
                  ₹{calculateTotal().toFixed(2)}
                </span>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap justify-center gap-4">
            <Button
              onClick={generatePDF}
              className="bg-[#2D3C92] hover:bg-[#1e2a6b] px-8 py-6 text-lg"
              data-testid="download-pdf-button"
            >
              <Download size={20} className="mr-2" />
              Download PDF
            </Button>
            <Button
              onClick={printInvoice}
              variant="outline"
              className="border-2 border-[#2D3C92] text-[#2D3C92] hover:bg-slate-50 px-8 py-6 text-lg"
              data-testid="print-invoice-button"
            >
              <Printer size={20} className="mr-2" />
              Print Invoice
            </Button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Billing;